/**
 *
 *  Organizations/Show/AdditionalQuestions/TopicsSelector
 *
 */

import React, { useMemo, useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Components
import Modal from 'components/Modal'

// Utils
import useCampaignTopicOptions from 'hooks/useCampaignTopicOptions'
import { COLORS } from 'global-styles'

const List = styled.div`
	border: 1px solid ${COLORS.grey_e};
	border-top: 0;
	max-height: 200px;
	min-height: 200px;
	overflow-x: auto;
`

const ListItem = styled.div`
	padding: 7px 10px;
	display: flex;
	align-items: center;
	border-bottom: 1px solid ${COLORS.grey_e};
	cursor: pointer;

	&:hover {
		background-color: ${COLORS.lightGrey};

		.selected {
			.check {
				color: ${COLORS.success} !important;
			}
		}

		.check {
			color: ${COLORS.grey_9} !important;
		}
	}

	span {
		margin-bottom: -1px;
	}
`

const CustomInput = styled.div`
	position: relative;

	input {
		padding: 10px 10px 10px 30px !important;
		height: 35px;
		font-size: 0.95rem;
		width: 100%;
		border: 1px solid ${COLORS.grey_e};

		&:focus {
			outline: none !important;
			box-shadow: none;
			border-color: ${COLORS.primary};
		}

		&.error {
			outline-color: ${COLORS.danger} !important;
			border-color: ${COLORS.danger} !important;
		}
	}

	.search {
		position: absolute;
		top: 11px;
		left: 12px;
	}

	.close {
		position: absolute;
		font-size: 1rem;
		top: 10px;
		right: 8px;
		cursor: pointer;
	}
`

const CheckboxStyle = styled.div`
	&.icons {
		position: relative;
		margin-right: 10px;
		height: 20px;
		width: 20px;

		.square {
			color: ${COLORS.grey_c};
			font-size: 16px;

			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}

		.check {
			color: transparent;
			font-size: 10px;

			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}

		&.selected {
			.square,
			.check {
				color: ${COLORS.success};
			}
		}

		&.partialy-selected {
			.check {
				color: ${COLORS.grey_c};
			}
		}
	}
`

function Checkbox({ selected, is_global = false }) {
	return (
		<CheckboxStyle className={`icons ${selected ? 'selected' : ''} ${is_global ? 'partialy-selected' : ''}`}>
			<FontAwesomeIcon icon={['far', 'square']} className="square" size="lg" />
			<FontAwesomeIcon icon={['far', 'check']} className="check" />
		</CheckboxStyle>
	)
}

export function FilterTopicSelector({ topics, setTopics, handleClose }) {
	const [search, setSearch] = useState('')
	const [selected_topics, setSelectedTopics] = useState(topics || [])

	const campaign_topic_options = useCampaignTopicOptions()

	const toggleTopic = (topic) => {
		setSelectedTopics((prev) => {
			if (prev.includes(topic)) {
				return prev.filter((i) => i !== topic)
			}

			return [...prev, topic]
		})
	}

	const searched_topics = useMemo(
		() => campaign_topic_options.filter((topic) => topic.text.toLowerCase().includes(search.toLowerCase())),
		[campaign_topic_options, search],
	)

	const onSave = () => {
		setTopics(selected_topics)
		handleClose()
	}

	return (
		<Modal show modalDidClose={handleClose} style={{ width: 500 }}>
			<div>
				<h3>Filter by Topics</h3>
				<p>Select Topics for Filter</p>

				<CustomInput>
					<input
						value={search}
						className={`form-control `}
						placeholder="Search Topic"
						onChange={(e) => {
							setSearch(e.target.value)
						}}
					/>

					<FontAwesomeIcon icon="search" size="sm" className="search" />

					{search.length > 0 && (
						<FontAwesomeIcon icon="times" className="close" onClick={() => setSearch('')} />
					)}
				</CustomInput>

				<List>
					{searched_topics.map((topic) => {
						const selected = selected_topics.includes(topic.value)

						return (
							<ListItem key={topic.id} onClick={() => toggleTopic(topic.value)}>
								<Checkbox selected={selected} />
								<span>{topic.text}</span>
							</ListItem>
						)
					})}

					{searched_topics.length === 0 && (
						<ListItem className="justify-content-center" style={{ cursor: 'default' }}>
							<span>No Topics To Select</span>
						</ListItem>
					)}
				</List>

				<button onClick={handleClose} className="btn btn-danger mt-4">
					Cancel
				</button>

				<button onClick={() => setSelectedTopics([])} className="btn btn-primary mt-4 ml-2">
					Clean Selected Topics
				</button>

				<button onClick={onSave} className="btn btn-success float-right mt-4">
					Filter by {selected_topics.length} Topics
				</button>
			</div>
		</Modal>
	)
}

function TopicSelector({ question, onEditQuestion }) {
	const { topics: selected_topics, global: is_global } = question
	const [search, setSearch] = useState('')
	const campaign_topic_options = useCampaignTopicOptions()

	const toggleTopic = (topic) => {
		let new_topics = [...selected_topics]

		if (new_topics.includes(topic)) {
			new_topics = new_topics.filter((i) => i !== topic)
		} else {
			new_topics = [...new_topics, topic]
		}

		onEditQuestion(new_topics, false)
	}

	const searched_topics = useMemo(
		() => campaign_topic_options.filter((topic) => topic.text.toLowerCase().includes(search.toLowerCase())),
		[campaign_topic_options, search],
	)

	return (
		<div style={{ marginLeft: '20px' }}>
			<ListItem
				onClick={() => onEditQuestion([], !is_global)}
				style={{
					marginBottom: '15px',
					border: `1px solid ${COLORS.grey_e}`,
				}}
			>
				<Checkbox selected={is_global} />
				<span>Set Question for All Campaign Topics</span>
			</ListItem>

			<CustomInput>
				<input
					value={search}
					className={`form-control `}
					placeholder="Search Topic"
					onChange={(e) => {
						setSearch(e.target.value)
					}}
				/>

				<FontAwesomeIcon icon="search" size="sm" className="search" />

				{search.length > 0 && <FontAwesomeIcon icon="times" className="close" onClick={() => setSearch('')} />}
			</CustomInput>

			<List>
				{searched_topics.map((topic) => {
					const selected = selected_topics.includes(topic.value)

					return (
						<ListItem key={topic.id} onClick={() => toggleTopic(topic.value)}>
							<Checkbox selected={selected} is_global={is_global} />
							<span>{topic.text}</span>
						</ListItem>
					)
				})}

				{searched_topics.length === 0 && (
					<ListItem className="justify-content-center" style={{ cursor: 'default' }}>
						<span>No Topics To Select</span>
					</ListItem>
				)}
			</List>
		</div>
	)
}

TopicSelector.propTypes = {
	question: PropTypes.object,
	onEditQuestion: PropTypes.func,
}

FilterTopicSelector.propTypes = {
	topics: PropTypes.array,
	setTopics: PropTypes.func,
	handleClose: PropTypes.func,
}

Checkbox.propTypes = {
	selected: PropTypes.bool,
	is_global: PropTypes.bool,
}

export default TopicSelector
